/**
 * @fileoverview Mock backend for the Sales CRM Edit, Archive and Restore Client commands.
 * @description Registers PATCH /sales/clients/{tenantId}, POST .../deactivate and POST .../restore on
 * the feature's shared mock adapter, answering in the backend's envelopes with its error codes
 * (Confluence 3.5.11.33-.35). All three are Super Admin only and need an `Idempotency-Key` (428 /
 * 4104). Edit quotes the `tp1.` version as `If-Match` (428 / 4104 missing, 412 / 4105 stale), is a
 * full replacement that keeps the secondary contact and appends a note rather than replacing the
 * notes, and refuses an archived client (409 / 4126), a registration number another bank holds (409
 * / 4102) and one cleared on a client that has it (400 / 0001). Archive and Restore refuse the wrong
 * state (409 / 4126, 409 / 4127). Like the other sales mocks it does not check MFA freshness. A
 * no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { checkCommand, listClients } from './active-clients-mock-guard'
import { allowedActionsOf, clientEtagOf, toClientDetails } from './active-clients-mock-projection'
import { bumpRevision, inactivate, reactivate } from './active-clients-mock-state'
import { invalidCreateBody, toCreateTenantInput } from './prospects-mock.service'
import { referenceFor, toBackendRating } from './sales-crm-mock-projection'
import { bodyOf, failReply, headerOf, okEnvelope } from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { patchTenant } from './tenants-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { CreateProspectRequest } from '@/features/sales/types/prospect.types'
import type { EditClientRequest } from '@/features/sales/types/sales-client.types'
import type { Tenant } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/**
 * @description The Create Prospect body an Edit Client body amounts to, so the shared body check and
 * translation to the Tenant store apply: a registration number left out is a placeholder (its real
 * value is written back separately), and no `sales` block keeps the lead source and rating.
 * @param {Tenant} tenant - The client being edited.
 * @param {EditClientRequest} body - The request body.
 * @returns {CreateProspectRequest} The equivalent body.
 */
function asProspectBody(tenant: Tenant, body: EditClientRequest): CreateProspectRequest {
  return {
    bank: { ...body.bank, registrationNumber: body.bank.registrationNumber?.trim() || 'none' },
    address: body.address,
    organisation: body.organisation,
    primaryContact: body.primaryContact,
    sales: body.sales ?? {
      leadSourceId: referenceFor('leadSources', tenant.leadSource).id,
      leadRating: toBackendRating(tenant.leadRating),
    },
  }
}

/**
 * @description Finds what the backend refuses in an Edit Client body.
 * @param {Tenant} tenant - The client being edited.
 * @param {EditClientRequest} body - The request body.
 * @returns {MockReply | null} The error to answer, or null when the edit is valid.
 */
function editBodyRefusal(tenant: Tenant, body: EditClientRequest): MockReply | null {
  const registration = body.bank.registrationNumber?.trim() ?? ''
  if (!registration && tenant.regNumber) {
    return failReply(400, '0001', 'The registration number of a live client cannot be cleared')
  }
  const invalid = invalidCreateBody(asProspectBody(tenant, body))
  if (invalid) return failReply(400, '0001', invalid)
  const usedElsewhere = listClients().some(
    (other) => other.id !== tenant.id && registration !== '' && other.regNumber === registration,
  )
  return usedElsewhere ? failReply(409, '4102', 'The registration number is already used') : null
}

/**
 * @description Edits a client the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The client as saved with its new version, or the backend's error.
 */
function editReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -1, 'EDIT')
  if (Array.isArray(checked)) return checked
  const { caller, tenant } = checked
  const ifMatch = headerOf(config, 'If-Match')
  if (!ifMatch) return failReply(428, '4104', 'If-Match is required')
  if (ifMatch !== clientEtagOf(tenant)) {
    return failReply(412, '4105', 'The client changed since it was read')
  }
  const body = bodyOf(config) as unknown as EditClientRequest
  const refusal = editBodyRefusal(tenant, body)
  if (refusal) return refusal

  const input = toCreateTenantInput(asProspectBody(tenant, body))
  const note = body.sales?.notes?.trim() ?? ''
  patchTenant(tenant.id, {
    ...input,
    regNumber: body.bank.registrationNumber?.trim() ?? '',
    notes: note ? `${tenant.notes}\n${note}`.trim() : tenant.notes,
    ...(body.sales ? {} : { leadSource: tenant.leadSource, leadRating: tenant.leadRating }),
    authorisedContact: {
      ...input.authorisedContact,
      officePhone: body.primaryContact.officePhone ?? '',
    },
  })
  bumpRevision(tenant.id)
  return [200, okEnvelope(toClientDetails(tenant, caller)), { etag: clientEtagOf(tenant) }]
}

/**
 * @description Archives a client the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The client's status and when it was archived, or the backend's error.
 */
function deactivateReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -2, 'DEACTIVATE')
  if (Array.isArray(checked)) return checked
  const { tenant } = checked
  return [
    200,
    okEnvelope({ tenantId: tenant.id, status: 'INACTIVE', inactivatedAt: inactivate(tenant.id) }),
  ]
}

/**
 * @description Restores an archived client the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The client's status, or the backend's error.
 */
function restoreReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -2, 'RESTORE')
  if (Array.isArray(checked)) return checked
  const { caller, tenant } = checked
  if (!allowedActionsOf(tenant, caller).includes('RESTORE')) {
    return failReply(409, '4127', 'The client is not archived')
  }
  reactivate(tenant.id)
  return [200, okEnvelope({ tenantId: tenant.id, status: 'ACTIVE' })]
}

if (salesMockAdapter) {
  salesMockAdapter.onPatch(/^\/sales\/clients\/[^/]+$/).reply(editReply)
  salesMockAdapter.onPost(/^\/sales\/clients\/[^/]+\/deactivate$/).reply(deactivateReply)
  salesMockAdapter.onPost(/^\/sales\/clients\/[^/]+\/restore$/).reply(restoreReply)
}
